import axiosInstance from "./axiosInstance";

export interface AnalysisType {
  id: number;
  name: string;
  category: string;
  description: string | null;
}

export interface AnalysisTypeByCategory {
  category: string;
  analyses: AnalysisType[];
}

// Libellés français pour les catégories
export const CATEGORY_LABELS: Record<string, string> = {
  HEMATOLOGIE: "HEMATOLOGIE",
  SEROLOGIE: "SEROLOGIE",
  BACTERIOLOGIE_PARASITOLOGIE: "BACTERIOLOGIE - PARASITOLOGIE",
  BIOCHIMIE_SANGUINE: "BIOCHIMIE SANGUINE",
  BIOCHIMIE_URINAIRE: "BIOCHIMIE URINAIRE",
  ENDOCRINOLOGIE: "ENDOCRINOLOGIE",
  MARQUEURS_TUMORAUX: "MARQ' TUMORAUX",
  DEPISTAGES: "DEPISTAGES",
};

// Cache pour les données d'analyses (évite les appels API répétés)
let cachedAnalysisTypes: AnalysisType[] | null = null;
let cachedAnalysisTypesGrouped: AnalysisTypeByCategory[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const isCacheValid = () => {
  return cacheTimestamp && (Date.now() - cacheTimestamp) < CACHE_DURATION;
};

/**
 * Récupère tous les types d'analyses (avec cache)
 */
export const getAllAnalysisTypes = async (): Promise<AnalysisType[]> => {
  if (cachedAnalysisTypes && isCacheValid()) {
    return cachedAnalysisTypes;
  }
  const response = await axiosInstance.get<AnalysisType[]>("/analysis-types");
  cachedAnalysisTypes = response.data;
  cacheTimestamp = Date.now();
  return response.data;
};

/**
 * Récupère les types d'analyses groupés par catégorie (avec cache)
 */
export const getAnalysisTypesGrouped = async (): Promise<AnalysisTypeByCategory[]> => {
  if (cachedAnalysisTypesGrouped && isCacheValid()) {
    return cachedAnalysisTypesGrouped;
  }
  const response = await axiosInstance.get<AnalysisTypeByCategory[]>("/analysis-types/grouped");
  cachedAnalysisTypesGrouped = response.data;
  cacheTimestamp = Date.now();
  return response.data;
};

/**
 * Invalide le cache (à appeler après une modification)
 */
export const invalidateAnalysisTypesCache = () => {
  cachedAnalysisTypes = null;
  cachedAnalysisTypesGrouped = null;
  cacheTimestamp = null;
};

/**
 * Récupère toutes les catégories disponibles
 */
export const getAllCategories = async (): Promise<string[]> => {
  const response = await axiosInstance.get<string[]>("/analysis-types/categories");
  return response.data;
};

/**
 * Récupère les analyses d'une catégorie spécifique
 */
export const getAnalysisByCategory = async (category: string): Promise<AnalysisType[]> => {
  const response = await axiosInstance.get<AnalysisType[]>(`/analysis-types/category/${category}`);
  return response.data;
};

/**
 * Récupère un type d'analyse par son ID
 */
export const getAnalysisTypeById = async (id: number): Promise<AnalysisType> => {
  const response = await axiosInstance.get<AnalysisType>(`/analysis-types/${id}`);
  return response.data;
};

/**
 * Crée un nouveau type d'analyse (Admin seulement)
 */
export const createAnalysisType = async (data: Omit<AnalysisType, "id">): Promise<AnalysisType> => {
  const response = await axiosInstance.post<AnalysisType>("/analysis-types", data);
  return response.data;
};

/**
 * Met à jour un type d'analyse (Admin seulement)
 */
export const updateAnalysisType = async (id: number, data: Omit<AnalysisType, "id">): Promise<AnalysisType> => {
  const response = await axiosInstance.put<AnalysisType>(`/analysis-types/${id}`, data);
  return response.data;
};

/**
 * Supprime un type d'analyse (Admin seulement)
 */
export const deleteAnalysisType = async (id: number): Promise<void> => {
  await axiosInstance.delete(`/analysis-types/${id}`);
};